import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardActionArea,
  CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SchoolIcon from "@mui/icons-material/School";
import ScienceIcon from "@mui/icons-material/Science";
import AssignmentIcon from "@mui/icons-material/Assignment";
import { IconButton, Tooltip, Chip } from "@mui/material";
import { CoeService } from "../../../services/dataService";

const CONTENT_TYPES = [
  {
    key: "fdp_content",
    label: "FDP Content",
    description: "Faculty Development Program resources",
    icon: <SchoolIcon sx={{ fontSize: 40 }} />,
    color: "#1565c0",
    bgcolor: "rgba(21, 101, 192, 0.08)",
  },
  {
    key: "lab_content",
    label: "Lab Content",
    description: "Laboratory manuals and practical resources",
    icon: <ScienceIcon sx={{ fontSize: 40 }} />,
    color: "#2e7d32",
    bgcolor: "rgba(46, 125, 50, 0.08)",
  },
  {
    key: "project_content",
    label: "Project Content",
    description: "Student projects and assessment materials",
    icon: <AssignmentIcon sx={{ fontSize: 40 }} />,
    color: "#7b1fa2",
    bgcolor: "rgba(123, 31, 162, 0.08)",
  },
];

const CoeAdminTypes = () => {
  const { academyId, domainId } = useParams();
  const navigate = useNavigate();

  const [corporateName, setCorporateName] = useState("Corporate");
  const [domainName, setDomainName] = useState("Domain");
  const [docCountMap, setDocCountMap] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [academyId, domainId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [corpRes, domainsRes, docsRes, countRes] = await Promise.allSettled([
        CoeService.getActiveAcademies(),
        CoeService.getDomainsByCorporate(academyId),
        CoeService.getDocumentsByDomain(domainId, academyId),
        CoeService.getDomainDocCount(domainId, academyId)
      ]);

      if (corpRes.status === "fulfilled" && corpRes.value?.data) {
        const academies = Array.isArray(corpRes.value.data) ? corpRes.value.data : [];
        const corp = academies.find((c) => String(c.academy_id) === String(academyId));
        if (corp) setCorporateName(corp.academy_brand || corp.academy_name || "Corporate");
      }

      if (domainsRes.status === "fulfilled" && domainsRes.value?.data) {
        const domain = domainsRes.value.data.find((d) => String(d.domain_id) === String(domainId));
        if (domain) setDomainName(domain.domain_name || "Domain");
      }

      if (docsRes.status === "fulfilled" && docsRes.value?.data) {
        const countMap = {};
        docsRes.value.data.forEach((doc) => {
          const type = doc.doc_type || "fdp_content";
          countMap[type] = (countMap[type] || 0) + 1;
        });
        setDocCountMap(countMap);
      }
    } catch (error) {
      console.error("Failed to fetch content type data", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 4, minHeight: "100vh" }}>
      {/* Header */}
      <Box display="flex" alignItems="center" mb={4}>
        <Tooltip title={`Back to COE selection`}>
          <IconButton onClick={() => navigate(`/admin-coe/${academyId}/${domainId}`)} sx={{ mr: 2 }}>
            <ArrowBackIcon color="primary" />
          </IconButton>
        </Tooltip>
        <Box>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 0.3 }}>
            COE → {corporateName} → {domainName} → COE
          </Typography>
          <Typography variant="h5" fontWeight="bold" sx={{ color: "#1976d2" }}>
            Select Content Type
          </Typography>
        </Box>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {CONTENT_TYPES.map((type) => {
            const count = docCountMap[type.key] || 0;
            return (
              <Grid item xs={12} sm={4} key={type.key}>
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: `1.5px solid ${count > 0 ? type.color : "#eeeeee"}`,
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      boxShadow: `0 12px 24px ${type.color}20`,
                      borderColor: type.color,
                      transform: "translateY(-4px)",
                    },
                  }}
                >
                  <CardActionArea
                    sx={{ p: 4, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}
                    onClick={() => navigate(`/admin-coe/${academyId}/${domainId}/coe/${type.key}`)}
                  >
                    <Box 
                      sx={{ 
                        bgcolor: type.bgcolor, 
                        p: 2, 
                        borderRadius: "20px",
                        color: type.color
                      }}
                    >
                      {type.icon}
                    </Box>
                    <Box textAlign="center">
                      <Typography variant="h6" fontWeight="800">
                        {type.label}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 1.5 }}>
                        {type.description}
                      </Typography>
                      <Chip 
                        label={count === 0 ? "No Documents" : `${count} Document${count > 1 ? "s" : ""}`}
                        size="small"
                        sx={{ 
                          bgcolor: count > 0 ? type.color : "#888",
                          color: "#fff",
                          fontWeight: 700
                        }}
                      />
                    </Box>
                  </CardActionArea>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
};

export default CoeAdminTypes;
